import React, { useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Rocket, X, CheckCircle2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

/**
 * Shown when a booking 403's because a city service isn't live yet.
 *
 * Props:
 *   open       boolean
 *   onClose    () => void
 *   city       string  (the city the user tried to book in)
 *   service    'rides' | 'bus' | 'logistics'
 *   phone      string  (optional — prefill from logged-in user)
 */
const CityWaitlistModal = ({ open, onClose, city, service, phone: initialPhone = '' }) => {
  const [phone, setPhone] = useState(initialPhone);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const submit = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 9) {
      toast({ title: 'Enter a valid phone number', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/public/city-waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ city, service, phone, email: email || null, name: name || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setDone(true);
      toast({
        title: data.already_on_list ? 'Already on the list' : "We'll let you know!",
        description: `We'll text you the moment ${service} goes live in ${city}.`,
      });
    } catch (err) {
      toast({ title: 'Failed to join waitlist', description: String(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="city-waitlist-modal"
    >
      <Card
        className="w-full max-w-md p-6 space-y-4 bg-white relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          onClick={onClose}
          data-testid="city-waitlist-close-btn"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="text-center py-6 space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h3 className="text-xl font-bold">You&apos;re on the list</h3>
            <p className="text-sm text-gray-600">
              We&apos;ll text you the moment <strong className="capitalize">{service}</strong> launches in <strong>{city}</strong>.
            </p>
            <Button onClick={onClose} data-testid="city-waitlist-done-btn">Got it</Button>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-pink-50">
                <Rocket className="h-6 w-6 text-pink-500" />
              </div>
              <div>
                <h3 className="text-xl font-bold">Launching soon in {city}</h3>
                <p className="text-sm text-gray-600 mt-1 capitalize">
                  {service} isn&apos;t live here yet. Drop your number and we&apos;ll text you the instant we open.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <Label>Phone</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+260 9XX XXX XXX"
                  data-testid="city-waitlist-phone-input"
                />
              </div>
              <div>
                <Label>Name (optional)</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Mwansa"
                  data-testid="city-waitlist-name-input"
                />
              </div>
              <div>
                <Label>Email (optional)</Label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  data-testid="city-waitlist-email-input"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Maybe later</Button>
              <Button
                onClick={submit}
                disabled={saving}
                className="bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="city-waitlist-submit-btn"
              >
                {saving ? 'Saving…' : 'Notify me'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default CityWaitlistModal;
